import { supabase } from '../supabase';
import { DEFAULT_SETTINGS } from '../../data/ldbDemoData';

export const LDB_COLLECTIONS = [
  'funders', 'grants', 'partners', 'subscribers', 'volunteers', 'followups',
];

function rowToItem(row) {
  return { id: row.id, ...(row.data || {}) };
}

function stripId(item) {
  const { id, ...rest } = item;
  return rest;
}

/** Load the full dashboard state (settings + all collections + activity log). */
export async function fetchState() {
  if (!supabase) throw new Error('Supabase not configured');

  const [settingsRes, recordsRes, activityRes] = await Promise.all([
    supabase.from('ldb_settings').select('data').eq('id', 1).maybeSingle(),
    supabase.from('ldb_records').select('id, collection, data').order('created_at', { ascending: true }),
    supabase.from('ldb_activity').select('id, action, detail, created_at').order('created_at', { ascending: false }).limit(200),
  ]);

  if (settingsRes.error) throw settingsRes.error;
  if (recordsRes.error) throw recordsRes.error;
  if (activityRes.error) throw activityRes.error;

  const state = {
    settings: { ...DEFAULT_SETTINGS, ...(settingsRes.data?.data || {}) },
    activityLog: (activityRes.data || []).map((r) => ({
      id: r.id, when: r.created_at, action: r.action, detail: r.detail,
    })),
  };
  LDB_COLLECTIONS.forEach((c) => { state[c] = []; });
  (recordsRes.data || []).forEach((row) => {
    if (!state[row.collection]) state[row.collection] = [];
    state[row.collection].push(rowToItem(row));
  });
  return state;
}

/** Insert a new record into a collection; returns the created item with its id. */
export async function createRecord(collection, item) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase
    .from('ldb_records')
    .insert({ collection, data: stripId(item) })
    .select('id, data')
    .single();
  if (error) throw error;
  return rowToItem({ id: data.id, ...data });
}

/** Update an existing record's data. */
export async function updateRecord(collection, id, item) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase
    .from('ldb_records')
    .update({ data: stripId(item), updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('id, data')
    .single();
  if (error) throw error;
  return rowToItem({ id: data.id, ...data });
}

export async function deleteRecord(collection, id) {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.from('ldb_records').delete().eq('id', id);
  if (error) throw error;
}

/** Upsert the singleton settings row. */
export async function saveSettings(settings) {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase
    .from('ldb_settings')
    .upsert({ id: 1, data: settings, updated_at: new Date().toISOString() });
  if (error) throw error;
  return { ...DEFAULT_SETTINGS, ...settings };
}

/** Append an activity log entry; returns the created entry. */
export async function logActivity(action, detail, actorEmail) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase
    .from('ldb_activity')
    .insert({ action, detail, actor_email: actorEmail || null })
    .select('id, action, detail, created_at')
    .single();
  if (error) throw error;
  return { id: data.id, when: data.created_at, action: data.action, detail: data.detail };
}

export async function clearActivity() {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.from('ldb_activity').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  if (error) throw error;
}
